/**
 * Xero workspace formatters — single source of truth for the page + lifted
 * cards. Timestamps are en-AU (the shipping locale, DESIGN.md §17); relative
 * time uses date-fns so "14 min ago" stays consistent with the rest of the app.
 */
import { formatDistanceToNowStrict } from 'date-fns';

/** Medium date + short time, en-AU. Em-dash glyph for empty (DESIGN.md §14). */
export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

/** "14 min ago" style relative label; em-dash glyph when absent. */
export const formatRelative = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${formatDistanceToNowStrict(date)} ago`;
};

/** Hours elapsed since a timestamp; Infinity when absent so ">24h" gates read clean. */
export const hoursSince = (value: string | null | undefined): number => {
  if (!value) return Number.POSITIVE_INFINITY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return Number.POSITIVE_INFINITY;
  return (Date.now() - date.getTime()) / 3_600_000;
};

/** Calendar-day bucket key (local) for grouping the Overview activity feed. */
export const dayKey = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toISOString().slice(0, 10);
};

/** Day header label for the Overview feed, e.g. "Sun, 4 May 2026". */
export const formatDayHeader = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};
